import { useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import { toPng, toSvg } from 'html-to-image';
import { Download, Printer } from 'lucide-react';

interface QRCodeCardProps {
  url: string;
  entityName: string;
  entityType: 'business' | 'trainer';
  entityId: string;
}

export function QRCodeCard({ url, entityName, entityType, entityId }: QRCodeCardProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const exportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (canvasRef.current) {
      QRCode.toCanvas(canvasRef.current, url, {
        width: 256,
        margin: 4,
        errorCorrectionLevel: 'M',
        color: { dark: '#1f2937', light: '#ffffff' },
      });
    }
  }, [url]);

  const handleDownloadPng = async () => {
    if (!exportRef.current) return;
    const dataUrl = await toPng(exportRef.current, { pixelRatio: 2 });
    const link = document.createElement('a');
    link.download = `spotto-qr-${entityType}-${entityId}.png`;
    link.href = dataUrl;
    link.click();
  };

  const handleDownloadSvg = async () => {
    if (!exportRef.current) return;
    const dataUrl = await toSvg(exportRef.current);
    const link = document.createElement('a');
    link.download = `spotto-qr-${entityType}-${entityId}.svg`;
    link.href = dataUrl;
    link.click();
  };

  const handlePrint = () => {
    window.open(`/qr/${entityType}/${entityId}?name=${encodeURIComponent(entityName)}`, '_blank');
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="font-semibold text-gray-900 mb-4">QR Kod</h3>

      <div ref={exportRef} className="inline-flex flex-col items-center bg-white p-6">
        <p className="text-lg font-bold text-blue-600 tracking-wider mb-3">SPOTTO</p>
        <canvas ref={canvasRef} />
        <p className="font-medium text-gray-900 mt-3">{entityName}</p>
        <p className="text-xs text-gray-400 mt-1">
          {entityType === 'business'
            ? 'Zeskanuj aby zarezerwowac'
            : 'Zeskanuj aby zobaczyc treningi'}
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <button
          onClick={handleDownloadPng}
          className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
        >
          <Download size={14} />
          Pobierz PNG
        </button>
        <button
          onClick={handleDownloadSvg}
          className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
        >
          <Download size={14} />
          Pobierz SVG
        </button>
        <button
          onClick={handlePrint}
          className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
        >
          <Printer size={14} />
          Drukuj
        </button>
      </div>
    </div>
  );
}
